/**
 * "Organisation details" — the member's own details in the organisation they are in now.
 * Real data since 6 Oct 2026: loaded with orgApi.attrsMine, saved with orgApi.valuesSet.
 *
 * Opened from the avatar menu as a 90% side panel (OrgDetailsPanel), or as a page at
 * /me/organisation. Laid out like the admin's Attributes page (5 Oct 2026): Basic information,
 * Standard attributes, Other details (custom).
 * Fields the organisation lets the member change are inputs; the rest show their value,
 * greyed. One Save changes for the lot. Their groups at the end.
 *
 * Values belong to the MEMBERSHIP, not the person: the same person can be "Store Manager,
 * South" in one organisation and "Trainer, North" in another. Switching organisation
 * switches every value and group here; only name and email come from the account.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { useUser } from '@clerk/clerk-react'
import { T } from '@/components/shell/orgTheme'
import { Btn, RoleChips } from '@/components/org/orgUi'
import { ROLE_LABEL } from '@/components/org/orgHelpers'
import { useOrgs } from '@/lib/useOrgs'
import { useAudienceCheck } from '@/components/launch/useAudienceCheck'
import { orgApi, type MyAttrs, type OrgRole } from '@/lib/orgApi'
import { BASIC_KEYS, displayValue, evaluate, STANDARD_ORDER, valuesProblem, type AttrDef, type LocationValue, type Value } from '@/lib/attributesModel'
import { attrs, nameOf, useAttrData, useAttrStatus, useFreshAttrs } from '@/lib/attributesData'
import { ValueInput } from './ValueInput'
import { SectionSkeleton } from './shared'
import { FormSection } from './FieldPreview'

const big = { padding: '11px 20px', fontSize: 15 }

/** The member's own details — shown in the side panel and at /me/organisation. */
export function MyOrgDetails() {
  const { user } = useUser()
  const orgs = useOrgs()
  const org = orgs.current
  // Their own view comes from attrs-mine: members cannot load the whole organisation's values.
  const [mine, setMine] = useState<{ orgId: string; data: MyAttrs } | null>(null)
  const [loadErr, setLoadErr] = useState('')
  const orgId = org?.id ?? null
  const load = (id: string) => orgApi.attrsMine(id).then(data => setMine({ orgId: id, data }), e => setLoadErr((e as Error).message))
  // Owners and admins edit everything — on their own profile too (6 Oct 2026). They see the
  // organisation's full data, as on Members → Edit details; only Email and Role stay fixed.
  const manager = orgs.isManager
  useEffect(() => { if (orgId && !manager) void load(orgId) }, [orgId, manager])
  if (!user) return null
  if (!org) return <p style={{ fontSize: 14, color: T.muted }}>You’re not in an organisation yet.</p>
  if (manager) {
    return (
      <MemberOrgDetails userId={user.id} email={user.primaryEmailAddress?.emailAddress ?? ''} roles={org.roles} orgName={org.name}
        account={{ firstName: user.firstName ?? '', lastName: user.lastName ?? '' }}
        saveName={(firstName, lastName) => user.update({ firstName, lastName }).then(() => undefined)}
        saveRoles={r => orgApi.setRoles(org.id, user.id, r).then(() => orgs.reload())} />
    )
  }
  if (!mine || mine.orgId !== org.id) {
    return loadErr
      ? <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 14.5, color: T.ink }}>{loadErr}<Btn onClick={() => { setLoadErr(''); void load(org.id) }}>Try again</Btn></div>
      : <DetailsSkeleton />
  }
  const d = mine.data
  return (
    <OrgDetailsForm key={org.id} mode="self" orgName={org.name}
      subject={{ userId: user.id, firstName: user.firstName ?? '', lastName: user.lastName ?? '', email: user.primaryEmailAddress?.emailAddress ?? '', roles: org.roles }}
      defs={d.defs} stored={d.values} groups={d.groups} nameOf={id => d.names[id] ?? 'Member'}
      saveValues={v => orgApi.valuesSet(org.id, user.id, v).then(() => load(org.id))}
      saveName={(firstName, lastName) => user.update({ firstName, lastName }).then(() => undefined)} />
  )
}

/** An Owner / Admin's view of one member (Members → Edit details), from the organisation's data. */
export function MemberOrgDetails({ userId, email, roles, orgName, account, saveName, saveRoles }: {
  userId: string; email: string; roles: OrgRole[]; orgName: string
  /** On an admin's OWN profile: their account name (editable, saved to their account). */
  account?: { firstName: string; lastName: string }
  saveName?: (firstName: string, lastName: string) => Promise<void>
  saveRoles?: (roles: OrgRole[]) => Promise<void>
}) {
  useFreshAttrs()   // they may have joined, or changed something, since the data was loaded
  const data = useAttrData()
  const { ready, error } = useAttrStatus()
  if (!ready) return error ? <span style={{ fontSize: 14.5, color: T.red }}>{error}</span> : <DetailsSkeleton />
  const person = data.people.find(p => p.id === userId)
  const values = person?.values ?? {}
  const groups = person ? data.groups.filter(g => !g.archived && (g.kind === 'auto' ? evaluate([person], g.rule).length === 1 : g.members.includes(userId))) : []
  return (
    <OrgDetailsForm mode="admin" orgName={orgName}
      subject={{ userId, email, roles, firstName: account?.firstName ?? String(values.first_name ?? ''), lastName: account?.lastName ?? String(values.last_name ?? '') }}
      defs={data.defs} stored={values} groups={groups} nameOf={id => nameOf(data, id)}
      saveValues={v => attrs.setValues(userId, v).then(() => undefined)} saveName={saveName} saveRoles={saveRoles} />
  )
}

export interface DetailsSubject { userId: string; firstName: string; lastName: string; email: string; roles: OrgRole[] }

/**
 * The Organisation details form, for one membership (5 Oct 2026; real data 6 Oct).
 *   self   — the member's own: what the organisation lets them change is editable; their name too
 *            (saved to their account via `saveName`).
 *   admin  — an Owner / Admin editing someone from Members → Edit details: every attribute,
 *            including Manager / Trainer / Coach and ones hidden from the member. Name, email and
 *            role stay read-only — the name is the person's own account, the role has its own
 *            rules in the member drawer.
 * The server re-checks every value and who may set it (api/_lib/attrActions.ts values-set).
 */
export function OrgDetailsForm({ mode, subject, orgName, defs, stored, groups, nameOf: personName, saveValues, saveName, saveRoles }: {
  mode: 'self' | 'admin'; subject: DetailsSubject; orgName: string
  defs: AttrDef[]; stored: Record<string, Value>; groups: Array<{ id: string; name: string }>
  nameOf: (id: string) => string
  saveValues: (values: Record<string, Value>) => Promise<void>
  saveName?: (firstName: string, lastName: string) => Promise<void>
  /** Owner / Admin only (6 Oct 2026): change roles here, by the member drawer's rules. */
  saveRoles?: (roles: OrgRole[]) => Promise<void>
}) {
  const [draft, setDraft] = useState<Record<string, Value>>({})
  // New details can take someone out of a group a course was launched to — asked first (23 Oct 2026),
  // on their own profile too.
  const audience = useAudienceCheck(useOrgs().current?.id)
  // Roles: the Owner always has every role (it moves only by transfer); anyone else's can change.
  const isOwner = subject.roles.includes('owner')
  const savedRoles = subject.roles.filter(r => r !== 'owner')
  const [roles, setRoles] = useState<OrgRole[] | null>(null)
  const rolesNow = roles ?? savedRoles
  const rolesChanged = !!roles && [...roles].sort().join() !== [...savedRoles].sort().join()
  const [saved, setSaved] = useState(false)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const self = mode === 'self'

  const account = { first_name: subject.firstName, last_name: subject.lastName }
  const values: Record<string, Value> = { ...stored, ...account, ...draft }
  // An admin sees every attribute; a member sees the ones made visible to them.
  const visible = (key: string) => defs.find(d => d.key === key && !d.archived && (!self || d.memberVisible))
  // Who edits what (6 Oct 2026):
  //   a learner (self)  — their name (saved to their ACCOUNT, so it shows in every organisation),
  //                       and the attributes set to "The member too";
  //   owner / admin     — every attribute, for anyone — on their own profile too. A name only
  //                       where it can be saved to that person's own account (`saveName`).
  //   Email and Role are fixed here for everyone.
  const canEdit = (d: AttrDef) => d.fromAccount ? !!saveName : self ? (!!d.memberEdit && d.type !== 'person') : true
  const role = (['owner', 'admin', 'creator', 'learner'] as const).filter(r => subject.roles.includes(r)).map(r => ROLE_LABEL[r]).join(', ')
  const dirty = Object.keys(draft).length > 0 || rolesChanged

  const save = async () => {
    const { first_name, last_name, ...rest } = draft
    setErr('')
    if (rolesChanged && rolesNow.length === 0) return setErr('Give them at least one role.')
    const bad = valuesProblem(defs, rest)
    if (bad) return setErr(bad)
    if (Object.keys(rest).length) return void audience.guard({ people: [{ userId: subject.userId, values: rest }] }, () => write(first_name, last_name, rest))
    return write(first_name, last_name, rest)
  }
  const write = async (first_name: Value, last_name: Value, rest: Record<string, Value>) => {
    setBusy(true)
    try {
      if (rolesChanged) await saveRoles?.(rolesNow)
      if (first_name !== undefined || last_name !== undefined) {
        const firstName = String(first_name ?? account.first_name).trim()
        const lastName = String(last_name ?? account.last_name).trim()
        if (!firstName) { setBusy(false); return setErr('First name can’t be empty.') }
        await saveName?.(firstName, lastName)
      }
      if (Object.keys(rest).length) await saveValues(rest)
      setDraft({}); setRoles(null); setSaved(true)
    } catch (e) {
      setErr((e as Error).message || 'Couldn’t save.')
    } finally { setBusy(false) }
  }
  const cell = (label: string, box: ReactNode, required?: boolean) => <Field key={label} big label={label} required={required}>{box}</Field>
  const field = (d: AttrDef) => d.type === 'location' ? (
    // Location is three linked fields — Country · State · City — each its own grid cell.
    canEdit(d)
      ? <ValueInput key={d.key} big def={d} value={values[d.key] ?? null} cell={(l, b) => cell(l, b, d.required && l === 'Country')}
          onChange={v => { setDraft(x => ({ ...x, [d.key]: v })); setSaved(false) }} />
      : <span key={d.key} style={{ display: 'contents' }}>
          {(['country', 'state', 'city'] as const).map(k => cell(k[0].toUpperCase() + k.slice(1), <ReadOnly big>{(values[d.key] as LocationValue | null)?.[k] || '—'}</ReadOnly>))}
        </span>
  ) : (
    <Field key={d.key} big label={d.label} required={d.required}>
      {canEdit(d)
        ? <ValueInput big def={d} value={values[d.key] ?? null} onChange={v => { setDraft(x => ({ ...x, [d.key]: v })); setSaved(false) }} />
        : <ReadOnly big>{displayValue(d, values[d.key], personName)}</ReadOnly>}
    </Field>
  )
  const grouped = new Set([...BASIC_KEYS, ...STANDARD_ORDER])
  const dob = visible('dob')
  const custom = defs.filter(d => !d.archived && (!self || d.memberVisible) && !grouped.has(d.key))
  const nameDefs = (['first_name', 'last_name'] as const).map(visible).filter((d): d is AttrDef => !!d)
  const mobile = visible('mobile')

  const saveBar = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 }}>
      {err && <span style={{ fontSize: 13.5, color: T.red }}>{err}</span>}
      {saved && !dirty && !err && <span style={{ fontSize: 13, color: T.green }}>Saved</span>}
      {dirty && <Btn onClick={() => { setDraft({}); setRoles(null); setErr('') }} style={big} disabled={busy}>Discard</Btn>}
      <Btn kind="primary" onClick={() => void save()} disabled={!dirty || busy} style={big}>{busy ? 'Saving…' : 'Save changes'}</Btn>
    </div>
  )
  const groupChips = groups.length === 0
    ? <span style={{ fontSize: 14.5, color: T.muted }}>{self ? 'You’re not in any groups yet.' : 'Not in any groups yet.'}</span>
    : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {groups.map(g => <span key={g.id} style={{ padding: '5px 12px', borderRadius: 999, background: T.tint, color: '#3B2A55', fontSize: 13 }}>{g.name}</span>)}
      </div>

  // Laid out like the admin's Attributes page — white cards (5 Oct 2026).
  // Three columns throughout, so rows line up card to card (5 Oct 2026). The order keeps related
  // fields on one row:  Designation · Hire date · Preferred language
  //                     Country · State · City
  //                     Manager · Trainer · Coach
  // Date of birth sits in Basic information with the other personal details.
  const standard = STANDARD_ORDER.map(visible).filter((d): d is AttrDef => !!d)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {audience.dialog}
      <span style={{ fontSize: 14.5, color: T.muted, marginTop: -4 }}>
        {self
          ? <>Your details in <strong style={{ color: T.ink, fontWeight: 600 }}>{orgName}</strong>. Your organisation decides which of these you can change.</>
          : <>{subject.email} · their details in <strong style={{ color: T.ink, fontWeight: 600 }}>{orgName}</strong>. Name and email belong to their own account.</>}
      </span>
      <FormSection three title="Basic information">
        {nameDefs.map(field)}
        <Field big label="Email address"><ReadOnly big>{subject.email}</ReadOnly></Field>
        {mobile && field(mobile)}
        {dob && field(dob)}
        <Field big label="Role">
          {saveRoles && !isOwner
            ? <span style={{ minHeight: 46, display: 'flex', alignItems: 'center' }}><RoleChips value={rolesNow} onChange={r => { setRoles(r); setSaved(false) }} /></span>
            : <ReadOnly big>{role || '—'}</ReadOnly>}
        </Field>
      </FormSection>
      {standard.length > 0 && <FormSection three title="Standard attributes">{standard.map(field)}</FormSection>}
      {custom.length > 0 && <FormSection three title="Other details">{custom.map(field)}</FormSection>}
      {saveBar}
      <FormSection title={self ? 'Your groups' : 'Groups'}><div style={{ gridColumn: '1 / -1' }}>{groupChips}</div></FormSection>
    </div>
  )
}

function Field({ label, required, children, big }: { label: string; required?: boolean; children: ReactNode; big?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: big ? 8 : 6, minWidth: 0 }}>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ flex: 1, fontSize: big ? 14.5 : 13.5, fontWeight: 500, color: T.dark }}>{label}{required && <span style={{ color: T.red, marginLeft: 2 }}>*</span>}</span>
      </span>
      {children}
    </div>
  )
}

/** A value the member can see but not change — greyed out, no explanation needed (5 Oct 2026). */
function ReadOnly({ children, big }: { children: ReactNode; big?: boolean }) {
  return (
    <span style={{ minHeight: big ? 46 : 40, display: 'flex', alignItems: 'center', padding: big ? '0 14px' : '0 12px', borderRadius: 10, background: '#F3F2F7', color: T.muted, cursor: 'not-allowed', fontSize: big ? 14.5 : 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</span>
  )
}

/** Their details form while it loads: Basic information, Standard attributes, Other details (7 Oct 2026). */
function DetailsSkeleton() {
  return (
    <div role="status" aria-label="Loading details" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <SectionSkeleton fields={6} />
      <SectionSkeleton fields={9} />
      <SectionSkeleton fields={3} />
    </div>
  )
}

